import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const faqData = [
  { question: 'How is Forsyt different from Gong?', answer: 'Gong is a revenue intelligence platform that analyzes sales conversations. Forsyt is a Revenue Operating System that sits on top of your entire GTM stack (CRM, calls, email, Slack) and prescribes the exact next action for each rep, each day.' },
  { question: 'Can Forsyt replace Gong?', answer: 'Forsyt complements Gong rather than replacing it. Gong tells you what happened in conversations; Forsyt tells every rep what to do next based on signals from all tools including Gong.' },
  { question: 'What does a Revenue OS do that Gong doesn\'t?', answer: 'A Revenue OS like Forsyt ingests data from CRM, call intelligence, email, and Slack to surface stalled deals, identify real blockers, and prescribe specific actions. Gong focuses on conversation analytics and coaching.' },
];

const ForsytVsGong = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Forsyt vs Gong - Revenue OS vs Revenue Intelligence"
        description="Gong analyzes sales conversations. Forsyt prescribes the exact next action. Compare Revenue Intelligence vs Revenue OS to see which your team actually needs."
        path="/vs-gong"
        faq={faqData}
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src="/assets/gt687tca.png"
                alt="Forsyt"
                className="h-8 w-auto"
              />
            </div>
            <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-5xl font-bold text-gray-900 mb-8">Forsyt vs Gong: Do You Need More Insights, Or an Operating System for Action?</h1>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Introduction</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Revenue leaders evaluating Gong today are not asking "Is Gong good?", they are asking "Is Gong enough?" Gong is a mature revenue intelligence platform; Forsyt is a Revenue Operating System designed to sit above your entire GTM stack and prescribe action.
              </p>
              <div className="p-6 bg-blue-50 border-l-4 border-blue-600 my-8">
                <p className="text-lg italic text-gray-800">
                  "Dashboards tell you what happened. A Revenue OS tells every rep what to do next."
                </p>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Use this post when a CRO, VP Sales, or Head of RevOps asks "How is Forsyt different from Gong?"
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">What Gong Actually Does Well</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>Positioning:</strong> Revenue Intelligence / Revenue AI OS.<br />
                <strong>Core strength:</strong> Recording, transcribing, and analyzing sales conversations at scale to give leaders insight.
              </p>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Gong's Superpowers</h3>
              <ul className="list-disc pl-6 space-y-3 text-gray-700">
                <li>Records and transcribes calls across voice and video, then analyzes them for patterns and insights.</li>
                <li>Surfaces deal and pipeline risk based on conversation signals, talk time, objections, and sentiment.</li>
                <li>Helps leaders coach reps using call snippets, scorecards, and conversation benchmarks.</li>
                <li>Powers dashboards that show what's happening in deals and what happened in the past quarter.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Gong is excellent when the question is:</strong> "What did we say, and how did that impact our pipeline?"
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Where Gong Stops Short For CROs</h2>
              <p className="text-gray-700 leading-relaxed mb-4">Even with Gong, most CROs still:</p>
              <ul className="list-disc pl-6 space-y-3 text-gray-700">
                <li>Export Salesforce and Gong data into spreadsheets to "get to the truth" before forecast calls.</li>
                <li>Spend hours each week interrogating reps, because insight does not automatically become consistent action.</li>
                <li>Struggle with tool fatigue: CRM + revenue intelligence + forecasting + enablement + spreadsheets.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4 font-semibold">
                Gong helps you see the movie; it does not run the playbook for you.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">What Forsyt Is, The Revenue OS Layer</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Forsyt positions itself as an AI‑native Revenue Operating System that ingests CRM, call intelligence (including Gong), email, and Slack to output a single stream of prioritized "what to do next" actions. Forsyt is intentionally built to sit on top of existing tools, not replace them.
              </p>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Forsyt's Core Design Principles</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Action over analytics</h4>
                  <p className="text-gray-700">Gong gives you insight; Forsyt turns cross‑tool signals into one directive per rep: "Do THIS next, with THIS talk track."</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">No rip‑and‑replace</h4>
                  <p className="text-gray-700">Forsyt integrates your CRM, Gong, email, and notes, then orchestrates actions on top of them.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Execution accountability</h4>
                  <p className="text-gray-700">Forsyt doesn't just flag risks; it tracks whether prescribed actions were executed and which ones actually move revenue.</p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Forsyt vs Gong, Side‑By‑Side</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-300">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 px-4 py-3 text-left font-bold">Dimension</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-bold">Gong (Revenue Intelligence)</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-bold">Forsyt (Revenue OS)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Primary job</td>
                      <td className="border border-gray-300 px-4 py-3">Capture & analyze customer conversations for insights.</td>
                      <td className="border border-gray-300 px-4 py-3">Orchestrate revenue actions across tools and teams.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Typical power user</td>
                      <td className="border border-gray-300 px-4 py-3">Sales leadership, enablement, RevOps.</td>
                      <td className="border border-gray-300 px-4 py-3">CRO, RevOps, frontline managers, and reps.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Output</td>
                      <td className="border border-gray-300 px-4 py-3">Dashboards, call libraries, deal risk flags.</td>
                      <td className="border border-gray-300 px-4 py-3">Prioritized "do this next" cards per rep + enforcement of process rules.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Forecast impact</td>
                      <td className="border border-gray-300 px-4 py-3">Better context and accuracy via call signals.</td>
                      <td className="border border-gray-300 px-4 py-3">Connects actions taken to forecast reliability and deal outcomes.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Relationship to CRM</td>
                      <td className="border border-gray-300 px-4 py-3">Augments CRM with conversation data.</td>
                      <td className="border border-gray-300 px-4 py-3">Uses CRM as a system of record but becomes the system of action.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Tool strategy</td>
                      <td className="border border-gray-300 px-4 py-3">Another core platform in the stack.</td>
                      <td className="border border-gray-300 px-4 py-3">An orchestration layer above CRM + Gong + others.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">When to Choose Which</h2>
              <ul className="space-y-4 text-gray-700">
                <li><strong>Choose Gong only</strong> if your biggest gap is conversation visibility and coaching, and you have strong internal discipline to convert insight into action.</li>
                <li><strong>Choose Forsyt + Gong</strong> if you already have Gong (or similar) and want a layer that enforces process, drives rep actions, and ties "what got done" to revenue outcomes.</li>
                <li><strong>Choose Forsyt as the core OS</strong> if your stack feels bloated, forecasts are still spreadsheet‑driven, and leadership wants a single place where actions, not dashboards, are the hero.</li>
              </ul>
            </section>

            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Next Step</h3>
              <p className="mb-6">
                Connect CRM + Gong into Forsyt and run a 3‑week "action experiment" on one region. Measure time saved in forecast calls and deals unblocked.
              </p>
              <a 
                href="https://calendar.app.google/nswB9pt6ALLZ8xoy7" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300"
              >
                Book Your Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ForsytVsGong;
